import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Ornament } from "./Ornament";
import type { ApiTokenCreatedResponse, ApiTokenPublic } from "../lib/types";

const fmt = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(iso.endsWith("Z") ? iso : `${iso}Z`))
    : "never";

export function ApiTokensSection() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<ApiTokenCreatedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const tokens = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => api.get<ApiTokenPublic[]>("/api/auth/api-tokens"),
  });

  const create = useMutation({
    mutationFn: (n: string) =>
      api.post<ApiTokenCreatedResponse>("/api/auth/api-tokens", { name: n }),
    onSuccess: (data) => {
      setMinted(data);
      setName("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.detail : "Failed to create key."),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/auth/api-tokens/${id}`),
    onSuccess: (_d, id) => {
      if (minted?.id === id) setMinted(null);
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setMinted(null);
    setCopied(false);
    create.mutate(name.trim());
  };

  const copy = async () => {
    if (!minted) return;
    await navigator.clipboard.writeText(minted.token);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2400);
  };

  return (
    <section className="mt-24">
      <div className="mb-6 flex items-center gap-4">
        <Ornament className="h-4 w-16 text-sienna" />
        <div className="label text-ink-walnut">Keys</div>
        <span className="hairline-ink flex-1" />
      </div>
      <p className="mb-10 font-body text-lg italic text-ink-walnut">
        API keys let an assistant read and write this household's recipes and
        plans on your behalf. Each key is shown once; revoke any you no longer
        use.
      </p>

      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <label className="label mb-2 block text-ink-walnut">Key name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Claude Code"
            className="field-input"
          />
        </div>
        {error && (
          <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="btn-ink"
          disabled={create.isPending || !name.trim()}
        >
          {create.isPending ? "Cutting…" : "Cut a new key"}
        </button>
      </form>

      {minted && (
        <div className="mt-12 border-l-2 border-sienna bg-parchment-deep/70 p-6 animate-fade-up">
          <div className="label mb-3 text-ink-walnut">
            {minted.name} — copy it now, it won't be shown again
          </div>
          <code className="block break-all font-mono text-sm leading-relaxed text-ink">
            {minted.token}
          </code>
          <div className="mt-5 flex items-center justify-between gap-4">
            <span className="label text-ink-muted">
              {copied ? "Copied to clipboard" : "Secret"}
            </span>
            <button type="button" onClick={copy} className="btn-ghost py-2.5">
              {copied ? "Copied" : "Copy key"}
            </button>
          </div>
        </div>
      )}

      {tokens.data && tokens.data.length > 0 && (
        <ul className="mt-14 divide-y divide-ink/15 border-y border-ink/15">
          {tokens.data.map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center justify-between gap-4 py-4"
            >
              <div>
                <div className="font-body text-lg text-ink">{t.name}</div>
                <div className="label mt-1 text-ink-muted">
                  made {fmt(t.created_at)} · last used {fmt(t.last_used_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Revoke "${t.name}"?`)) revoke.mutate(t.id);
                }}
                disabled={revoke.isPending}
                className="label text-ink transition-colors duration-200 hover:text-sienna"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
